import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Users, Calendar, Crown, User } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import type { ChatRoomInfo } from "@/lib/types";

const THEME_COLORS = {
    "--accent-green": "hsl(128,60%,54%)",
    "--light-gradient-from": "hsl(130, 40%, 75%)",
    "--light-gradient-to": "hsl(0, 0%, 98%)",
    "--subtle-gradient-from": "#0A3832",
    "--subtle-gradient-to": "#000000",
} as React.CSSProperties;

interface RoomMember {
    userId: string;
    displayName: string;
    isAdmin: boolean;
    joinedAt: string;
}

interface RoomInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    room: ChatRoomInfo | null;
}

export function RoomInfoModal({ isOpen, onClose, room }: RoomInfoModalProps) {
    const [members, setMembers] = useState<RoomMember[]>([]);
    const [loading, setLoading] = useState(false);

    // ✅ Memoize to prevent re-creation
    const loadRoomMembers = useCallback(async () => {
        if (!room) return;
        setLoading(true);

        try {
            const response = await apiRequest("GET", `/api/rooms/${room.roomId}/members`);
            const memberData = await response.json();

            // ✅ Normalize possible nested user objects
            const normalized = memberData.map((m: any) => ({
                userId: m.userId || m.user?._id || "",
                displayName: m.displayName || m.user?.name || "Unknown",
                isAdmin: !!m.isAdmin,
                joinedAt: m.joinedAt || "",
            }));

            setMembers(normalized);
        } catch (error) {
            console.error("❌ Failed to load room members:", error);
        } finally {
            setLoading(false);
        }
    }, [room]);

    // ✅ Fetch members only when modal opens
    useEffect(() => {
        if (isOpen && room) loadRoomMembers();
    }, [isOpen, room, loadRoomMembers]);

    if (!room) return null;

    const adminNames = members
        .filter((m) => m.isAdmin)
        .map((m) => m.displayName)
        .join(", ");
    const createdDate = new Date(room.createdAt).toLocaleDateString();

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <div style={THEME_COLORS}>
                <DialogContent
                    style={THEME_COLORS}
                    className="max-w-sm rounded-2xl border border-gray-300 dark:border-gray-800
          bg-[#f8fafc]
          dark:bg-[#0f172a]
          shadow-2xl text-center p-8 transition-all duration-300 animate-in fade-in-0 zoom-in-95"
                >
                    <DialogHeader>
                        <DialogTitle className="flex flex-col items-center justify-center space-y-4">
                            <Avatar className="w-20 h-20 border-2 border-[var(--accent)] shadow-md">
                                <AvatarFallback className="bg-[var(--accent)] text-white text-3xl">{room.type === "group" ? <Users className="w-8 h-8" /> : <User className="w-8 h-8" />}</AvatarFallback>
                            </Avatar>

                            <div>
                                <h2 className="text-2xl font-semibold flex flex-col items-center justify-center dark:text-white text-gray-900">{room.name}</h2>
                                <Badge variant="secondary" className="mt-2 text-xs flex flex-col items-center justify-center bg-[var(--accent)] text-white dark:bg-[var(--accent)]  dark:text-white ">
                                    {room.type === "direct" ? "Direct Message (DM)" : room.type}
                                </Badge>
                            </div>
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Details and members for this chat room.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-6 space-y-5 text-left">
                        <div className="flex items-center space-x-3">
                            <Calendar className="w-5 h-5 text-[var(--accent)] dark:text-[var(--accent)]" />
                            <div>
                                <p className="text-sm font-medium">Created</p>
                                <p className="text-sm text-gray-600 dark:text-gray-300">{createdDate}</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <Users className="w-5 h-5 text-[var(--accent)] dark:text-[var(--accent)]" />
                            <div>
                                <p className="text-sm font-medium">Members</p>
                                <p className="text-sm text-gray-600 dark:text-gray-300">{loading ? "Loading..." : `${members.length} active`}</p>
                            </div>
                        </div>

                        {/* <div className="flex items-center space-x-3">
                            <Crown className="w-5 h-5 text-green-500" />
                            <div>
                                <p className="text-sm font-medium">Admin{members.filter((m) => m.isAdmin).length > 1 ? "s" : ""}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-300">{loading ? "Loading..." : adminNames || "No admins assigned"}</p>
                            </div>
                        </div> */}
                    </div>
                </DialogContent>
            </div>
        </Dialog>
    );
}
